import React, { useMemo, useState } from "react";
import { Box, Card, Typography, useI18nContext } from "@procore/core-react";
import type { ProjectDetailsActivitiesProps } from "../types";
import {
   ActivityCategory,
   IntegratedEntity,
} from "@laborchart-modules/common/dist/postgres/schemas/common/enums";
import { dateSortOptions, projectSpecificDateSortOptions } from "@/react/shared/constants";
import { Order } from "@laborchart-modules/common/dist/reql-builder/query-definitions";
import "../../style.css";
import { AuthAction, usePermissionContext } from "@/react/providers/permission-context-provider";
import { ProjectActivitiesInfoCard } from "./project-activities-info-card";
import { useFindActivityPaginatedQuery } from "@/react/components/common/queries/queries";
import ActivityList from "../../activities/activity-list";
import type { ActivitiesResponseProps, ActivitiesSpecificOptionType } from "../../types";
import { formatCurrency, formatDate } from "../../helper";
import { CannedAssignmentMessage } from "../../activities/activity-helper";

const ACTIVITIES_LIMIT = 25;

// Render helper for subcategory activities
const renderSubcategoryActivity = (
   activity: ActivitiesResponseProps,
   I18n: ReturnType<typeof useI18nContext>,
) => {
   if (activity.action_type === "update") {
      return (
         <Box display="flex" style={{ marginTop: "10px" }}>
            <Typography intent="body" color="gray50">
               <span className="light-text">
                  {I18n.t(
                     "views.company.workforce_planning.project_details_activities.activity_action.subcategory_updated",
                     { property: activity.meta?.property_name },
                  )}
               </span>
               <span className="dark-highlighted-text">{` ${activity?.meta?.old_val} `}</span>
               <span className="light-text">
                  {I18n.t(
                     "views.company.workforce_planning.project_details_activities.activity_action.to",
                  )}
               </span>
               <span className="dark-highlighted-text">{` ${activity?.meta?.new_val}`}</span>
            </Typography>
         </Box>
      );
   }

   return (
      <Box display="flex" style={{ marginTop: "10px" }}>
         <Typography intent="body" color="gray50">
            <span className="light-text">
               {activity?.action_type === "create"
                  ? `${I18n.t(
                       "views.company.workforce_planning.project_details_activities.activity_action.added",
                    )} `
                  : `${I18n.t(
                       "views.company.workforce_planning.project_details_activities.activity_action.removed",
                    )} `}
            </span>
            <span className="dark-highlighted-text">{`${activity?.subject_3_name} `}</span>
            <span className="light-text">
               {activity?.action_type === "create"
                  ? `${I18n.t(
                       "views.company.workforce_planning.project_details_activities.activity_action.to",
                    )} `
                  : `${I18n.t(
                       "views.company.workforce_planning.project_details_activities.activity_action.from",
                    )} `}
            </span>
            <span className="dark-highlighted-text">{`${activity?.subject_2_name}`}</span>
         </Typography>
      </Box>
   );
};

const REQUEST_ACTION_KEYS: Record<string, { request: string; equipmentRequest: string }> = {
   update: { request: "updated_request", equipmentRequest: "updated_equipment_request" },
   create: { request: "added_request_status", equipmentRequest: "added_equipment_request_status" },
   delete: { request: "deleted_request", equipmentRequest: "deleted_equipment_request" },
};

const getRequestActionTranslationKey = (
   actionType: string | undefined,
   isEquipmentRequest: boolean,
): string => {
   const keys = REQUEST_ACTION_KEYS[actionType ?? "delete"] ?? REQUEST_ACTION_KEYS.delete;
   return isEquipmentRequest ? keys.equipmentRequest : keys.request;
};

// Render helper for request activities
const renderRequestActivity = (
   activity: ActivitiesResponseProps,
   I18n: ReturnType<typeof useI18nContext>,
) => {
   const isEquipmentRequest = activity.subject_2_type === "equipment_requests";
   const translationPrefix =
      "views.company.workforce_planning.project_details_activities.activity_action";
   const actionKey = getRequestActionTranslationKey(activity.action_type, isEquipmentRequest);
   const translationParams =
      activity.action_type === "create" ? { status: `${activity?.meta?.status_name}` } : {};

   if (activity.action_type === "update") {
      return (
         <Box display="flex" style={{ marginTop: "10px" }}>
            <Typography intent="body" color="gray50">
               <span className="light-text">{I18n.t(`${translationPrefix}.${actionKey}`)}</span>
               <span className="dark-highlighted-text">{` ${formatDate(
                  activity?.meta?.start_day,
                  I18n.currentLocale(),
               )} `}</span>
               <span className="light-text">{I18n.t(`${translationPrefix}.to`)}</span>
               <span className="dark-highlighted-text">
                  {formatDate(activity?.meta?.end_day, I18n.currentLocale())}
               </span>
            </Typography>
         </Box>
      );
   }

   return (
      <Box display="flex" style={{ marginTop: "10px" }}>
         <Typography intent="body" color="gray50">
            <span className="light-text">
               {`${I18n.t(`${translationPrefix}.${actionKey}`, translationParams)} `}
            </span>
            <span className="dark-highlighted-text">{`${formatDate(
               activity?.meta?.start_day,
               I18n.currentLocale(),
            )} `}</span>
            <span className="light-text">{I18n.t(`${translationPrefix}.to`)}</span>
            <span className="dark-highlighted-text">
               {formatDate(activity?.meta?.end_day, I18n.currentLocale())}
            </span>
         </Typography>
      </Box>
   );
};

// Render activity content based on category
const renderProjectActivityContent = (
   activity: ActivitiesResponseProps,
   I18n: ReturnType<typeof useI18nContext>,
) => {
   switch (activity.category) {
      case projectSpecificDateSortOptions[1].value:
         return (
            <Box display="flex" style={{ marginTop: "10px" }}>
               <Typography intent="body" color="gray50">
                  <span className="light-text">
                     {activity?.action_type === "create"
                        ? `${I18n.t(
                             "views.company.workforce_planning.project_details_activities.activity_action.added",
                          )} `
                        : `${I18n.t(
                             "views.company.workforce_planning.project_details_activities.activity_action.removed",
                          )} `}
                  </span>
                  <span className="dark-highlighted-text">{`${activity?.subject_2_name} `}</span>
                  <span className="light-text">
                     {activity?.action_type === "create"
                        ? `${I18n.t(
                             "views.company.workforce_planning.project_details_activities.activity_action.to",
                          )} `
                        : `${I18n.t(
                             "views.company.workforce_planning.project_details_activities.activity_action.attachment_from",
                          )} `}
                  </span>
                  <span className="dark-highlighted-text">{`${activity?.subject_1_name}`}</span>
               </Typography>
            </Box>
         );
      case projectSpecificDateSortOptions[2].value:
         return (
            <Box display="flex" style={{ marginTop: "10px" }}>
               <Typography intent="body" color="gray50">
                  <span className="light-text">
                     {activity?.action_type === "create"
                        ? `${I18n.t(
                             "views.company.workforce_planning.project_details_activities.activity_action.added",
                          )} `
                        : `${I18n.t(
                             "views.company.workforce_planning.project_details_activities.activity_action.removed",
                          )} `}
                  </span>
                  <span className="dark-highlighted-text">{`${activity?.subject_2_name} `}</span>
                  <span className="light-text">
                     {activity?.action_type === "create"
                        ? `${I18n.t(
                             "views.company.workforce_planning.project_details_activities.activity_action.to",
                          )} `
                        : `${I18n.t(
                             "views.company.workforce_planning.project_details_activities.activity_action.from",
                          )} `}
                  </span>
                  <span className="dark-highlighted-text">{`${activity?.subject_1_name}`}</span>
               </Typography>
            </Box>
         );
      case projectSpecificDateSortOptions[3].value:
         return renderRequestActivity(activity, I18n);
      case projectSpecificDateSortOptions[5].value:
         return (
            <Box display="flex" style={{ marginTop: "10px" }}>
               <Typography intent="body" color="gray50">
                  <span className="light-text">
                     {activity?.action_type === "create"
                        ? `${I18n.t(
                             "views.company.workforce_planning.project_details_activities.activity_action.added_note",
                          )} `
                        : `${I18n.t(
                             "views.company.workforce_planning.project_details_activities.activity_action.removed_note",
                          )} `}
                  </span>
                  <span className="dark-highlighted-text">{`${activity?.subject_1_name}`}</span>
               </Typography>
            </Box>
         );
      case projectSpecificDateSortOptions[6].value:
         return (
            <Box display="flex" style={{ marginTop: "10px" }}>
               <Typography intent="body" color="gray50">
                  <span className="light-text">
                     {activity?.action_type === "create"
                        ? `${I18n.t(
                             "views.company.workforce_planning.project_details_activities.activity_action.added",
                          )} `
                        : `${I18n.t(
                             "views.company.workforce_planning.project_details_activities.activity_action.removed",
                          )} `}
                  </span>
                  <span className="dark-highlighted-text">{`${
                     typeof activity?.subject_3_name === "string"
                        ? activity.subject_3_name
                        : activity.subject_3_name?.first
                  } ${
                     typeof activity?.subject_3_name === "string"
                        ? activity.subject_3_name
                        : activity.subject_3_name?.last
                  } `}</span>
                  <span className="light-text">
                     {activity?.action_type === "create"
                        ? `${I18n.t(
                             "views.company.workforce_planning.project_details_activities.activity_action.to_role",
                          )} `
                        : `${I18n.t(
                             "views.company.workforce_planning.project_details_activities.activity_action.from_role",
                          )} `}
                  </span>
                  <span className="dark-highlighted-text">{`${activity?.subject_2_name}`}</span>
               </Typography>
            </Box>
         );
      case projectSpecificDateSortOptions[7].value:
         return (
            <Box display="flex" style={{ marginTop: "10px" }}>
               <Typography intent="body" color="gray50">
                  <span className="light-text">
                     {activity?.action_type === "create"
                        ? `${I18n.t(
                             "views.company.workforce_planning.project_details_activities.activity_action.added",
                          )} `
                        : `${I18n.t(
                             "views.company.workforce_planning.project_details_activities.activity_action.removed",
                          )} `}
                  </span>
                  <span className="dark-highlighted-text">{`${activity?.subject_2_name} `}</span>
                  <span className="light-text">
                     {activity?.action_type === "create"
                        ? `${I18n.t(
                             "views.company.workforce_planning.project_details_activities.activity_action.to",
                          )} `
                        : `${I18n.t(
                             "views.company.workforce_planning.project_details_activities.activity_action.from",
                          )} `}
                  </span>
                  <span className="dark-highlighted-text">{`${activity?.subject_1_name}`}</span>
               </Typography>
            </Box>
         );
      case projectSpecificDateSortOptions[8].value:
         return (
            <Box display="flex" style={{ marginTop: "10px" }}>
               <Typography intent="body" color="gray50">
                  <span className="light-text">
                     {activity?.action_type === "create"
                        ? `${I18n.t(
                             "views.company.workforce_planning.project_details_activities.activity_action.added_wage_override",
                          )} `
                        : `${I18n.t(
                             "views.company.workforce_planning.project_details_activities.activity_action.removed_wage_override",
                          )} `}
                  </span>
                  <span className="dark-highlighted-text">{`${formatCurrency(
                     activity?.meta?.rate,
                     I18n,
                  )} `}</span>
                  <span>
                     {activity?.action_type === "create"
                        ? `${I18n.t(
                             "views.company.workforce_planning.project_details_activities.activity_action.created_wage_override",
                          )} `
                        : `${I18n.t(
                             "views.company.workforce_planning.project_details_activities.activity_action.from_the",
                          )} `}
                  </span>
                  <span className="dark-highlighted-text">{`${activity?.subject_2_name} `}</span>
                  <span className="light-text">
                     {I18n.t(
                        "views.company.workforce_planning.project_details_activities.activity_action.position",
                     )}
                  </span>
               </Typography>
            </Box>
         );
      case ActivityCategory.PROJECT_COST_CODES_LABELS:
         return renderSubcategoryActivity(activity, I18n);
      case projectSpecificDateSortOptions[4].value:
         return <ProjectActivitiesInfoCard activity={activity} />;
      case ActivityCategory.PROJECT:
         return (
            <Box display="flex" style={{ marginTop: "10px" }}>
               <Typography intent="body" color="gray50">
                  <span className="light-text">
                     {activity?.action_type === "create"
                        ? `${I18n.t(
                             "views.company.workforce_planning.project_details_activities.activity_action.added_project",
                          )} `
                        : `${I18n.t(
                             "views.company.workforce_planning.project_details_activities.activity_action.removed",
                          )} `}
                  </span>
                  <span className="dark-highlighted-text">{`${activity?.subject_1_name} `}</span>
               </Typography>
            </Box>
         );
      case ActivityCategory.PROJECT_TAGS_ATTACHMENTS:
         return (
            <Box display="flex" style={{ marginTop: "10px" }}>
               <Typography intent="body" color="gray50">
                  <span className="light-text">
                     {activity?.action_type === "create"
                        ? `${I18n.t(
                             "views.company.workforce_planning.project_details_activities.activity_action.added_attachment",
                          )} `
                        : `${I18n.t(
                             "views.company.workforce_planning.project_details_activities.activity_action.removed_attachment",
                          )} `}
                  </span>
                  <span className="dark-highlighted-text">{`${activity?.subject_3_name} `}</span>
                  <span className="light-text">
                     {activity?.action_type === "create"
                        ? `${I18n.t(
                             "views.company.workforce_planning.project_details_activities.activity_action.to_tag",
                          )} `
                        : `${I18n.t(
                             "views.company.workforce_planning.project_details_activities.activity_action.from_tag",
                          )} `}
                  </span>
                  <span className="dark-highlighted-text">{`${activity?.subject_2_name}`}</span>
               </Typography>
            </Box>
         );
      case ActivityCategory.PROJECT_CANNED_ASSIGNMENT_MESSAGE:
         return <CannedAssignmentMessage activity={activity} I18n={I18n} />;
      default:
         return null;
   }
};

export const ProjectDetailsActivities: React.FC<ProjectDetailsActivitiesProps> = ({
   projectId,
}) => {
   const I18n = useI18nContext();
   const { checkAuthAction } = usePermissionContext();

   // Sorting state (client-side)
   const [sortOption, setSortOption] = useState(dateSortOptions[1]);

   // Category filter state
   const [sortProjectSpecificOption, setSortProjectSpecificOption] =
      useState<ActivitiesSpecificOptionType>(projectSpecificDateSortOptions[0]);

   // Build dropdown options based on permissions
   const projectSpecificOptions = useMemo(() => {
      const permissionMap: Record<string, AuthAction> = {
         "1": AuthAction.VIEW_PROJECT_ATTACHMENTS,
         "5": AuthAction.VIEW_PROJECT_NOTES,
         "6": AuthAction.VIEW_PROJECT_ROLES,
         "7": AuthAction.VIEW_PROJECT_TAGS,
         "8": AuthAction.VIEW_PROJECT_WAGE_OVERRIDES,
      };

      return projectSpecificDateSortOptions.map((option) => {
         const requiredPermission = permissionMap[option.id];
         if (requiredPermission && !checkAuthAction(requiredPermission)) {
            return { ...option, show: false };
         }
         return option;
      });
   }, [checkAuthAction]);

   // Determine which categories to fetch based on filter selection
   const categoriesToFetch = useMemo(() => {
      if (sortProjectSpecificOption.id === "0") {
         return [];
      }
      return [sortProjectSpecificOption.value];
   }, [sortProjectSpecificOption]);

   // Single query with useInfiniteQuery
   const {
      data: activitiesData,
      isLoading: activitiesLoading,
      fetchNextPage,
      isFetchingNextPage,
   } = useFindActivityPaginatedQuery(
      projectId,
      IntegratedEntity.PROJECTS,
      categoriesToFetch,
      ACTIVITIES_LIMIT,
   );

   // Derive values from query
   const allActivities = useMemo(() => {
      return activitiesData?.pages.flatMap((page) => page?.data ?? []) ?? [];
   }, [activitiesData?.pages]);

   const totalPossible = activitiesData?.pages?.[0]?.pagination?.total_possible ?? 0;

   // Sort activities client-side
   const sortedActivities = useMemo(() => {
      if (!allActivities.length) return [];
      return [...allActivities].sort((a, b) => {
         const dateA = new Date(a.created_at).getTime();
         const dateB = new Date(b.created_at).getTime();
         return sortOption.value === Order.ASCENDING ? dateA - dateB : dateB - dateA;
      });
   }, [allActivities, sortOption]);

   return (
      <Card style={{ marginBottom: "5px" }}>
         <ActivityList
            title="views.company.workforce_planning.project_details_activities.title"
            subtext="views.company.workforce_planning.project_details_activities.activities_sub_text"
            sortActivitiesOptions={projectSpecificOptions}
            sortActivitiesSpecificOption={sortProjectSpecificOption}
            onActivitiesSortChange={setSortProjectSpecificOption}
            sortOptions={dateSortOptions}
            sortOption={sortOption}
            onSortChange={setSortOption}
            activities={sortedActivities}
            renderActivityContent={(activity) => renderProjectActivityContent(activity, I18n)}
            loadMoreHandler={fetchNextPage}
            limit={totalPossible}
            loadMoreLabel="views.company.workforce_planning.project_details_activities.lode_more"
            emptyTitle={I18n.t(
               "views.company.workforce_planning.project_details_activities.activities_empty_state_title",
            )}
            emptyDescription={I18n.t(
               "views.company.workforce_planning.project_details_activities.activities_empty_state_description",
            )}
            isLoading={activitiesLoading}
            isFetchingNextPage={isFetchingNextPage}
         />
      </Card>
   );
};
